import Phaser from 'phaser';
import { MetaProgression, MetaUpgrade } from '../systems/MetaProgression';
import { SoundManager } from '../systems/SoundManager';

export class MetaShopScene extends Phaser.Scene {
  private metaProgression!: MetaProgression;
  private soundManager!: SoundManager;
  private selectedUpgradeIndex: number = 0;
  private upgradeCards: Phaser.GameObjects.Container[] = [];
  private catTreatsText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'MetaShopScene' });
  }

  create(): void {
    this.metaProgression = new MetaProgression();
    this.soundManager = new SoundManager(this);

    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Background
    const bg = this.add.rectangle(centerX, centerY, width, height, 0x1a1a1a);

    // Grid pattern
    const graphics = this.add.graphics();
    graphics.lineStyle(1, 0x333333, 0.2);
    for (let x = 0; x < width; x += 40) {
      graphics.lineBetween(x, 0, x, height);
    }
    for (let y = 0; y < height; y += 40) {
      graphics.lineBetween(0, y, width, y);
    }

    // Title
    const title = this.add.text(centerX, 50, '🐱 META UPGRADES 🐱', {
      fontSize: '48px',
      color: '#4CAF50',
      fontStyle: 'bold'
    });
    title.setOrigin(0.5);

    this.tweens.add({
      targets: title,
      scale: { from: 0.9, to: 1 },
      alpha: { from: 0, to: 1 },
      duration: 500,
      ease: 'Back.out'
    });

    // Cat Treats display
    const stats = this.metaProgression.getStats();
    const treatsPanel = this.add.rectangle(centerX, 110, 300, 50, 0x2a2a2a, 0.9);
    treatsPanel.setStrokeStyle(3, 0xFFD700);

    this.catTreatsText = this.add.text(centerX, 110, `🍪 Cat Treats: ${stats.catTreats}`, {
      fontSize: '24px',
      color: '#FFD700',
      fontStyle: 'bold'
    });
    this.catTreatsText.setOrigin(0.5);

    // Stats summary
    const statsText = this.add.text(centerX, 155,
      `Runs: ${stats.totalRuns} | Best Time: ${Math.floor(stats.longestSurvival / 60)}:${(stats.longestSurvival % 60).toString().padStart(2, '0')} | Max Level: ${stats.highestLevel}`,
      {
        fontSize: '16px',
        color: '#888888'
      }
    );
    statsText.setOrigin(0.5);

    // Create upgrade cards
    this.createUpgradeCards();

    // Back button
    const backButton = this.add.rectangle(100, height - 50, 160, 50, 0x4CAF50);
    backButton.setInteractive({ useHandCursor: true });
    backButton.setStrokeStyle(3, 0x66BB6A);

    const backText = this.add.text(100, height - 50, '← BACK', {
      fontSize: '20px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    });
    backText.setOrigin(0.5);

    backButton.on('pointerover', () => {
      backButton.setFillStyle(0x66BB6A);
      this.tweens.add({
        targets: backButton,
        scale: 1.05,
        duration: 200
      });
    });

    backButton.on('pointerout', () => {
      backButton.setFillStyle(0x4CAF50);
      this.tweens.add({
        targets: backButton,
        scale: 1,
        duration: 200
      });
    });

    backButton.on('pointerdown', () => {
      this.soundManager.playMenuClick();
      this.scene.start('MainMenuScene');
    });

    // Keyboard input
    this.input.keyboard?.on('keydown-ESC', () => {
      this.soundManager.playMenuClick();
      this.scene.start('MainMenuScene');
    });

    // Fade in
    this.cameras.main.fadeIn(500);
  }

  private createUpgradeCards(): void {
    const upgrades = this.metaProgression.getAllUpgrades();
    const cardWidth = 280;
    const cardHeight = 180;
    const padding = 20;
    const columns = 3;

    const startX = (this.cameras.main.width - (columns * (cardWidth + padding)) + padding) / 2;
    const startY = 220;

    upgrades.forEach((upgrade, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = startX + col * (cardWidth + padding) + cardWidth / 2;
      const y = startY + row * (cardHeight + padding);

      const card = this.createUpgradeCard(upgrade, x, y, cardWidth, cardHeight);
      this.upgradeCards.push(card);
    });
  }

  private createUpgradeCard(
    upgrade: MetaUpgrade,
    x: number,
    y: number,
    width: number,
    height: number
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const isMaxLevel = upgrade.currentLevel >= upgrade.maxLevel;
    const cost = this.metaProgression.getUpgradeCost(upgrade.id);
    const canAfford = this.metaProgression.getStats().catTreats >= cost;

    // Background
    const bg = this.add.rectangle(0, 0, width, height, 0x2a2a2a, 0.9);
    const borderColor = isMaxLevel ? 0xFFD700 : canAfford ? 0x4CAF50 : 0x666666;
    bg.setStrokeStyle(3, borderColor);
    bg.setInteractive({ useHandCursor: !isMaxLevel });
    container.add(bg);

    // Icon
    const icon = this.add.text(0, -60, upgrade.icon, {
      fontSize: '40px'
    });
    icon.setOrigin(0.5);
    container.add(icon);

    // Name
    const name = this.add.text(0, -20, upgrade.name, {
      fontSize: '18px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      align: 'center'
    });
    name.setOrigin(0.5);
    name.setWordWrapWidth(width - 20);
    container.add(name);

    // Description
    const desc = this.add.text(0, 10, upgrade.description, {
      fontSize: '14px',
      color: '#CCCCCC',
      align: 'center'
    });
    desc.setOrigin(0.5);
    desc.setWordWrapWidth(width - 20);
    container.add(desc);

    // Level indicator
    const levelText = this.add.text(0, 40, `Level: ${upgrade.currentLevel}/${upgrade.maxLevel}`, {
      fontSize: '16px',
      color: '#FFD700'
    });
    levelText.setOrigin(0.5);
    container.add(levelText);

    // Cost or Max Level text
    let costText: Phaser.GameObjects.Text;
    if (isMaxLevel) {
      costText = this.add.text(0, 65, 'MAX LEVEL', {
        fontSize: '18px',
        color: '#FFD700',
        fontStyle: 'bold'
      });
    } else {
      costText = this.add.text(0, 65, `Cost: ${cost} 🍪`, {
        fontSize: '18px',
        color: canAfford ? '#4CAF50' : '#FF6B6B',
        fontStyle: 'bold'
      });
    }
    costText.setOrigin(0.5);
    container.add(costText);

    // Hover and click
    if (!isMaxLevel) {
      bg.on('pointerover', () => {
        bg.setFillStyle(0x3a3a3a, 0.9);
        this.tweens.add({
          targets: container,
          scale: 1.05,
          duration: 200,
          ease: 'Power2'
        });
      });

      bg.on('pointerout', () => {
        bg.setFillStyle(0x2a2a2a, 0.9);
        this.tweens.add({
          targets: container,
          scale: 1,
          duration: 200,
          ease: 'Power2'
        });
      });

      bg.on('pointerdown', () => {
        this.purchaseUpgrade(upgrade);
      });
    }

    // Initial fade in animation
    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      alpha: 1,
      duration: 400,
      delay: 100 * Math.floor(this.upgradeCards.length / 3),
      ease: 'Power2'
    });

    return container;
  }

  private purchaseUpgrade(upgrade: MetaUpgrade): void {
    if (this.metaProgression.purchaseUpgrade(upgrade.id)) {
      this.soundManager.playLevelUp();

      // Update Cat Treats display
      const stats = this.metaProgression.getStats();
      this.catTreatsText.setText(`🍪 Cat Treats: ${stats.catTreats}`);

      // Recreate all cards to reflect new state
      this.upgradeCards.forEach(card => card.destroy());
      this.upgradeCards = [];
      this.createUpgradeCards();

      // Success feedback
      const successText = this.add.text(
        this.cameras.main.centerX,
        this.cameras.main.centerY,
        `${upgrade.icon} UPGRADED! ${upgrade.icon}`,
        {
          fontSize: '48px',
          color: '#4CAF50',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 6
        }
      );
      successText.setOrigin(0.5);
      successText.setDepth(1000);

      this.tweens.add({
        targets: successText,
        scale: { from: 0.5, to: 1.5 },
        alpha: { from: 1, to: 0 },
        duration: 1500,
        ease: 'Power2',
        onComplete: () => {
          successText.destroy();
        }
      });
    } else {
      this.soundManager.playPlayerHit();

      // Not enough treats feedback
      const errorText = this.add.text(
        this.cameras.main.centerX,
        this.cameras.main.centerY,
        'NOT ENOUGH CAT TREATS!',
        {
          fontSize: '32px',
          color: '#FF0000',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 4
        }
      );
      errorText.setOrigin(0.5);
      errorText.setDepth(1000);

      this.tweens.add({
        targets: errorText,
        y: errorText.y - 50,
        alpha: { from: 1, to: 0 },
        duration: 1000,
        ease: 'Power2',
        onComplete: () => {
          errorText.destroy();
        }
      });
    }
  }
}
